import type { OnboardingRecipient } from './api';

/**
 * Canonical list of placeholder variables the onboarding email renderer
 * understands. Templates wrap these in double braces, e.g. {{firstName}}.
 *
 * This is the single source of truth for the variable set; `api.ts`
 * re-exports it as `ONBOARDING_TEMPLATE_VARIABLES` for the template editor's
 * hint chips.
 */
export const ONBOARDING_VARIABLES = [
  'firstName',
  'fullName',
  'nominatorEmail',
  'projectTitle',
  'companyName',
  'projectCodes',
  'categories',
  'originalCategory',
  'newCategory',
  'loginUrl',
  'setupUrl',
] as const;

export type OnboardingVariable = (typeof ONBOARDING_VARIABLES)[number];

/** The resolved value plus a human label naming where the data comes from. */
export interface ResolvedVariable {
  value: string;
  source: string;
}

/**
 * Resolve the variable → { value, source } map for a given recipient, using
 * the recipient's real data where available and clearly-labelled sample data
 * otherwise (the recipient record has no project/company/category-change
 * fields, so those are illustrative).
 */
export function resolveOnboardingVariables(
  recipient: OnboardingRecipient
): Record<string, ResolvedVariable> {
  const firstName = recipient.display_name.trim().split(/\s+/)[0] || recipient.display_name;
  const firstCategory = recipient.categories[0];

  return {
    firstName: { value: firstName, source: 'Recipient name · user.DisplayName' },
    fullName: { value: recipient.display_name, source: 'Recipient · user.DisplayName' },
    nominatorEmail: { value: recipient.email, source: 'Recipient · user.Email' },
    email: { value: recipient.email, source: 'Recipient · user.Email' },
    projectTitle: { value: 'Riverside Cultural Centre', source: 'Entry · nomination.ProjectTitle' },
    companyName: {
      value: 'Studio Example Architects',
      source: 'Entry · nomination.ArchitectCompanyName',
    },
    projectCodes: { value: recipient.project_codes.join(', '), source: 'Entry codes' },
    categories: { value: recipient.categories.join(', '), source: 'Entry categories' },
    originalCategory: {
      value: firstCategory ?? 'Completed Buildings - Civic',
      source: 'Entry category',
    },
    newCategory: {
      value: firstCategory ?? 'Completed Buildings - Office',
      source: 'New category',
    },
    loginUrl: { value: window.location.origin, source: 'Portal login link' },
    setupUrl: {
      value: `${window.location.origin}/getting-started?code=••••••`,
      source: "One-time set-password link · minted per recipient at send (90-day expiry)",
    },
  };
}

const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/** Escape a string for safe injection into an HTML attribute / text node. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Replace every {{var}} token with a highlighted <span> showing the resolved
 * value and a title tooltip naming its data source. Unknown tokens are left
 * untouched. Intended for admin-only preview rendering of our own template
 * content via dangerouslySetInnerHTML.
 */
export function renderOnboardingPreview(
  html: string,
  vars: Record<string, ResolvedVariable>
): string {
  return html.replace(TOKEN_RE, (match, name: string, offset: number, full: string) => {
    const resolved = vars[name];
    if (!resolved) return match;
    // If the token sits inside an HTML tag — e.g. href="{{loginUrl}}" —
    // wrapping it in a <span> would break the attribute and leak the raw URL
    // as text. Detect "inside a tag" by checking whether the nearest angle
    // bracket before us is an unclosed '<'. In that case substitute the plain
    // escaped value; only highlight tokens that appear in text content.
    const insideTag = full.lastIndexOf('<', offset) > full.lastIndexOf('>', offset);
    if (insideTag) return escapeHtml(resolved.value);
    return `<span class="waf-var" title="${escapeHtml(resolved.source)}" style="background:#FCEFB4;border-radius:3px;padding:0 2px;">${escapeHtml(
      resolved.value
    )}</span>`;
  });
}
